import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeft } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { RouteTransitionLogo } from '../components/RouteTransitionLogo';
import { getDefaultRoute } from '../utils/defaultRoute';
import { persistAuthSession } from '../utils/authStorage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ResetPasswordPage({ onLogin }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error('Invalid reset link');
      return;
    }
    if (!password || !confirmPassword) {
      toast.error('Please fill in both fields');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/reset-password`, { token, password });
      await persistAuthSession(res.data.access_token, res.data.user);
      onLogin?.(res.data.user);
      toast.success('Password updated! You are signed in.');
      navigate(getDefaultRoute(res.data.user), { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
      className="app-shell premium-page"
      style={{
        minHeight: '100vh',
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundColor: 'var(--app-bg)',
        position: 'relative',
        paddingLeft: 24, paddingRight: 24, paddingBottom: 40,
      }}
    >
      <button
        onClick={() => navigate('/login')}
        aria-label="Go back"
        data-testid="reset-back-button"
        style={{ position: 'absolute', top: 'calc(env(safe-area-inset-top, 0px) + 20px)', left: 20, background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
      >
        <ChevronLeft size={26} color="var(--text)" strokeWidth={1.75} />
      </button>

      <div className="auth-fade-up" style={{ animationDelay: '0ms' }}>
        <RouteTransitionLogo width={120} height={108} dataTestId="reset-shared-logo" style={{ marginTop: 56 }} />
      </div>

      <h1
        className="auth-fade-up"
        style={{ fontFamily: "'Amarante', serif", fontSize: 30, fontWeight: 400, color: 'var(--text)', textAlign: 'center', marginTop: 14, marginBottom: 8, animationDelay: '100ms' }}
      >
        Set a New Password
      </h1>
      <p
        className="auth-fade-up"
        style={{ fontFamily: "'Outfit', sans-serif", fontSize: 14, color: 'var(--text-2)', textAlign: 'center', maxWidth: 320, lineHeight: 1.5, marginBottom: 28, animationDelay: '120ms' }}
      >
        Choose a strong password you haven&apos;t used before.
      </p>

      <form
        id="reset-form"
        onSubmit={handleSubmit}
        className="premium-card auth-fade-up"
        style={{ width: '100%', maxWidth: 360, padding: '28px 24px', animationDelay: '200ms' }}
      >
        <div style={{ marginBottom: 16 }}>
          <label htmlFor="reset-password" className="auth-label">New Password</label>
          <input
            id="reset-password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            data-testid="reset-password-input"
            className="auth-input"
          />
        </div>
        <div>
          <label htmlFor="reset-confirm" className="auth-label">Confirm Password</label>
          <input
            id="reset-confirm"
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="••••••••"
            data-testid="reset-confirm-password-input"
            className="auth-input"
          />
        </div>
      </form>

      <motion.button
        type="submit"
        form="reset-form"
        disabled={loading}
        whileHover={loading ? {} : { scale: 1.01 }}
        whileTap={loading ? {} : { scale: 0.99 }}
        className="auth-primary-btn auth-fade-up"
        data-testid="reset-password-submit-button"
        style={{ maxWidth: 360, marginTop: 24, animationDelay: '300ms' }}
      >
        {loading ? 'Updating…' : 'Update Password'}
      </motion.button>

      <button
        type="button"
        onClick={() => navigate('/login')}
        data-testid="back-to-login-link"
        className="auth-link auth-fade-up"
        style={{ marginTop: 24, display: 'flex', alignItems: 'center', gap: 6, animationDelay: '360ms' }}
      >
        <ChevronLeft size={16} /> Back to Login
      </button>
    </motion.div>
  );
}
